import { Box, Checkbox, SxProps, TableCell, TableHead, TableRow, TableSortLabel, Tooltip } from '@mui/material';
import { Theme } from '@mui/material/styles';
import React, { ReactNode } from 'react';

import useLocales from 'hooks/useLocales';

// ----------------------------------------------------------------------

const visuallyHidden = {
    border: 0,
    margin: -1,
    padding: 0,
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
    clip: 'rect(0 0 0 0)',
} as const;

// ----------------------------------------------------------------------

export type HeadCell = {
    id: string;
    label?: string;
    tooltip?: string | NonNullable<ReactNode>;
    align?: 'right' | 'left' | 'center';
    width?: string;
    minWidth?: string;
};

type Props = {
    order?: 'asc' | 'desc';
    orderBy?: string;
    headLabel: HeadCell[];
    rowCount?: number;
    numSelected?: number;
    onSort?: (id: string) => void;
    onSelectAllRows?: (checked: boolean) => void;
    sx?: SxProps<Theme>;
};

export default function TableHeadCustom({
    order,
    orderBy,
    rowCount = 0,
    headLabel,
    numSelected = 0,
    onSort,
    onSelectAllRows,
    sx,
}: Props) {
    const { translate } = useLocales();

    return (
        <TableHead sx={sx}>
            <TableRow>
                {onSelectAllRows && (
                    <TableCell padding='checkbox'>
                        <Checkbox
                            indeterminate={numSelected > 0 && numSelected < rowCount}
                            checked={rowCount > 0 && numSelected === rowCount}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                onSelectAllRows(event.target.checked)
                            }
                        />
                    </TableCell>
                )}

                {headLabel.map(headCell => (
                    <Tooltip key={headCell.id} title={headCell.tooltip || ''} placement='bottom' arrow>
                        <TableCell
                            align={headCell.align || 'left'}
                            sortDirection={orderBy === headCell.id ? order : false}
                            sx={{ width: headCell.width, minWidth: headCell.minWidth, textTransform: 'capitalize' }}
                        >
                            {onSort ? (
                                <TableSortLabel
                                    hideSortIcon
                                    active={orderBy === headCell.id}
                                    direction={orderBy === headCell.id ? order : 'asc'}
                                    onClick={() => onSort(headCell.id)}
                                >
                                    {translate(headCell.label as any)}

                                    {orderBy === headCell.id ? (
                                        <Box sx={{ ...visuallyHidden }}>
                                            {translate(order === 'desc' ? 'sorted_descending' : 'sorted_ascending')}
                                        </Box>
                                    ) : null}
                                </TableSortLabel>
                            ) : (
                                <>{translate(headCell.label as any)}</>
                            )}
                        </TableCell>
                    </Tooltip>
                ))}
            </TableRow>
        </TableHead>
    );
}
